import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'

type WorkspaceRedirectResolver = (newOrgHandle: string | null) => string | null

interface WorkspaceRedirectContextValue {
  register: (resolver: WorkspaceRedirectResolver | null) => void
  resolveRedirect: WorkspaceRedirectResolver
}

const WorkspaceRedirectContext = createContext<WorkspaceRedirectContextValue>({
  register: () => undefined,
  resolveRedirect: () => null,
})

export const WorkspaceRedirectProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => {
  const resolverRef = useRef<WorkspaceRedirectResolver | null>(null)

  const value = useMemo<WorkspaceRedirectContextValue>(
    () => ({
      register: (resolver): void => {
        resolverRef.current = resolver
      },
      resolveRedirect: (newOrgHandle): string | null => resolverRef.current?.(newOrgHandle) ?? null,
    }),
    [],
  )

  return (
    <WorkspaceRedirectContext.Provider value={value}>{children}</WorkspaceRedirectContext.Provider>
  )
}

const useRegisterWorkspaceRedirect = (resolve: WorkspaceRedirectResolver): void => {
  const { register } = useContext(WorkspaceRedirectContext)
  const resolveRef = useRef(resolve)
  resolveRef.current = resolve

  useEffect(() => {
    register((newOrgHandle) => resolveRef.current(newOrgHandle))
    return (): void => register(null)
  }, [register])
}

export const WorkspaceChangeRedirect = ({
  to,
  children,
}: {
  to: string
  children: React.ReactNode
}): React.ReactElement => {
  useRegisterWorkspaceRedirect(() => to)
  return <>{children}</>
}

export const OrganizationScopedPage = ({
  root,
  children,
}: {
  root: string
  children: React.ReactNode
}): React.ReactElement => {
  useRegisterWorkspaceRedirect((newOrgHandle) =>
    newOrgHandle ? `/organizations/${newOrgHandle}/${root}` : null,
  )
  return <>{children}</>
}

export const useWorkspaceRedirectResolver = (): WorkspaceRedirectResolver =>
  useContext(WorkspaceRedirectContext).resolveRedirect

const ORG_SCOPED_PATH_RE = /(^.*\/organizations\/)([^/]+)(\/.*)?$/

export const resolveWorkspaceDestination = (
  pathname: string,
  search: string,
  newOrgHandle: string | null,
  resolveRedirect: WorkspaceRedirectResolver,
): string | null => {
  const registered = resolveRedirect(newOrgHandle)
  if (registered) return registered
  if (!newOrgHandle) return null
  const match = pathname.match(ORG_SCOPED_PATH_RE)
  if (!match || match[2] === newOrgHandle) return null
  return `${match[1]}${newOrgHandle}${match[3] ?? ''}${search}`
}
